import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  DefaultDadoField,
  UserImportDataSourceAshby,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { useState } from 'react';
import shortid from 'shortid';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { usePromise } from '../../../../../../hooks/usePromise';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import {
  getAshbyFields,
  useAshbyConfig
} from '../../../../../Org/pages/Integrations/integrations/ashby/service';
import { CustomImportMappingFormWithAutocomplete } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem, IntegrationNotConfigured } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.ASHBY;

const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: '$FULL_NAME$',
    otherKey: 'name'
  },
  {
    dadoKey: DefaultDadoField.EMAIL,
    otherKey: 'primaryEmailAddress.value'
  },
  {
    dadoKey: DefaultDadoField.LOCATION,
    otherKey: 'location.locationSummary'
  }
];

export const AshbySetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { id: userId } = useCurrentUser();
  const [config, loading, error] = useAshbyConfig(orgId, userId);
  const isSetup = !!(config?.data.apiKey && config?.data.apiKey);
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [mappings, setMappings] = useState<UserImportMapping[]>(
    DEFAULT_MAPPINGS
  );

  const [ashbyFields, loadingFields, errorFields] = usePromise(async () => {
    const result = await getAshbyFields(orgId);
    return result;
  });

  if (loading || loadingFields) {
    return (
      <DialogContent>
        <Loader height={250} />
      </DialogContent>
    );
  }
  if (error || errorFields) {
    return <SomethingWentWrong height={150} />;
  }
  if (!config) {
    return null;
  }

  if (!isSetup) {
    return (
      <IntegrationNotConfigured integration={META.name} onClose={onClose} />
    );
  }

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>

        {ashbyFields && (
          <CustomImportMappingFormWithAutocomplete
            additionalCompositeFields={['$FULL_NAME$']}
            userFields={userFields}
            integrationName={META.name}
            name={t('Default Field Mapping')}
            mappings={mappings}
            onChange={(mappings) => setMappings(mappings)}
            fields={ashbyFields.map((f) => ({
              name: f.path,
              label: f.label
            }))}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            const nextSource: Doc<UserImportDataSourceAshby> = {
              id: shortid(),
              collection: FS.userImportDataSources,
              data: {
                orgId: orgId,
                type: UserImportSourceType.ASHBY,
                status: UserImportDataSourceStatus.PENDING,
                detailedStatus: '',
                lastUpdate: null,
                createdBy: userId,
                createdAt: now(),
                customMappings: mappings.filter((m) => !!m.otherKey)
              }
            };
            await setDoc(nextSource);
            onClose();
          }}
        >
          {t('Add source')}
        </Button>
      </DialogActions>
    </>
  );
};

export const ImportSourceRowAshby = ({
  d
}: {
  d: Doc<UserImportDataSourceAshby>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
